import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { errorToastMessage } from '../../../components/layout/toast-root/toast';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useNotFound } from '../../../hooks/useNotFound';
import { useGetFilePaths } from '../../../redux/api/appConfigApi';
import {
  useLazyGetProgramCollectionById,
  usePostPutProgramCollection,
} from '../../../redux/api/programsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { FileVisibility } from '../../../shared/constants';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/files/firebase-storage-utils';
import { uploadFilesUtils } from '../../../utils/files/upload-files-utils';
import { getProgramValidation } from './FormikData';

export function useEditProgramCollectionComponent(programId?: string) {
  const [postPutProgram] = usePostPutProgramCollection();
  const [getProgramCollection, { data: programCollection, error }] =
    useLazyGetProgramCollectionById();
  const { back } = useNavigationStack();
  const { t } = useTranslation();
  const [avatar, setAvatar] = useState(null);
  const { data: filePaths } = useGetFilePaths();
  const { data: user } = useGetMyDetails();
  const [isLoading, setIsLoading] = useState(false);

  useNotFound(!!error);

  useEffect(() => {
    if (programId) getProgramCollection(programId);
  }, [programId]);

  const formik = useFormik({
    validateOnChange: false,
    enableReinitialize: true,
    validationSchema: getProgramValidation(t),
    initialValues: {
      title: programCollection?.title || '',
      description: programCollection?.description || '',
    },
    onSubmit,
  });

  async function onSubmit(update) {
    if (avatar && (!user?.uid || !user?.id || !filePaths)) {
      return;
    }

    setIsLoading(true);
    let avatarFile;

    try {
      if (avatar) {
        avatarFile = await uploadFileAndCreateAppFile(
          avatar,
          filePaths.programs,
          user.uid,
          user.id,
          FileVisibility.public,
        );
      }

      await postPutProgram({
        id: programCollection?.id,
        body: {
          ...update,
          avatar: avatarFile
            ? {
                id: avatarFile.id,
                name: avatarFile.name,
                mimeType: avatarFile.mimeType,
                size: avatarFile.size,
                storageLink: avatarFile.storageLink,
                previewStorageLink: avatarFile.previewStorageLink,
              }
            : undefined,
        },
      }).unwrap();

      back();
    } catch (error) {
      console.error(
        'Error uploading program collection avatar or saving collection:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
      if (avatarFile) {
        await deleteFilesFromFirebase(
          [avatarFile?.storageLink, avatarFile?.previewStorageLink].filter(
            Boolean,
          ),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  return {
    formik,
    program: programCollection,
    isLoading,
    avatar,
    setAvatar,
  };
}
